/**
 * The slice check catalog — reviewed code (ADR-0088 §3, ADR-0090 §4–§5), docs/v10.9/slice.md.
 *
 * 19 authored rules (18 GCP + the Cloudflare D1 gap rule) with content and fixture hashes, and the
 * first catalog release `gacpro-checks@2026.10.1`. Migration 675 (scripts/content/gen-slice-rules.mjs)
 * loads exactly these records into `check_rules` / `check_rule_versions` / `catalog_releases`, at
 * release state Tested: CI (this package's tests) proved the fixtures; Verified needs a named
 * reviewer who is not the author (ADR-0046 SoD), recorded through the editor route.
 *
 * LM-26 (2026-10-14): the second release `gacpro-checks@2026.10.2` keeps those 19 unchanged and adds
 * R20 (privileged roles granted just in time, Privileged Access Manager) at Tested — migration 684,
 * same generator. 675 stays exactly the first release (SLICE_RULE_SET_V1).
 *
 * LM-29 (2026-10-14): the third release `gacpro-checks@2026.10.3` replaces R05 version 1 with version 2
 * (zero privileged principals passes when a live Privileged Access Manager entitlement can grant an
 * admin role) — migration 686, same generator. 684 stays exactly the second release (SLICE_RULE_SET_V2).
 */
import { fixturesSha256, ruleContentSha256 } from './canonical.js';
import { testRuleFixtures } from './fixtures.js';
import { GCP_SLICE_RULES } from './slice/gcp.js';
import { R20 } from './slice/gcp-pam.js';
import { R05_V2 } from './slice/gcp-r05-v2.js';
import { D1_JURISDICTION_RULE } from './slice/cloudflare-d1.js';
import { AUTHOR } from './slice/kit.js';
import { SLICE_SOURCES, SLICE_SEARCH_RECORDS, SLICE_UNSUPPORTED } from './slice/sources.js';
export { SLICE_SOURCES, SLICE_SEARCH_RECORDS, SLICE_UNSUPPORTED };
/** The Org Policy constraints R08–R16 read (the GCP collector reads only those the approved mapping library expects). */
export { ORG_POLICY_RULE_CONSTRAINTS } from './slice/gcp.js';
/** LM-22: which GCP rules run per project vs once on the organization under an organization connection. */
export { GCP_PROJECT_LEVEL_RULES, GCP_ORGANIZATION_LEVEL_RULES } from './slice/gcp.js';
/** LM-26: R20's observation paths and its duration bound (the GCP collector writes these). */
export { PAM_PATHS, JIT_MAX_DURATION_S } from './slice/gcp-pam.js';
export { AUTHOR as SLICE_RULE_AUTHOR };
/** The 19 rules of the first release `gacpro-checks@2026.10.1` (migration 675). Frozen: never edit, version instead. */
export const SLICE_RULE_SET_V1 = [...GCP_SLICE_RULES, D1_JURISDICTION_RULE];
/** LM-26: rules added by the second release `gacpro-checks@2026.10.2` (migration 684). */
export const SLICE_RULE_SET_V2_ADDED = [R20];
/** The 20 rules of the second release `gacpro-checks@2026.10.2` (migration 684). Frozen. */
export const SLICE_RULE_SET_V2 = [...SLICE_RULE_SET_V1, ...SLICE_RULE_SET_V2_ADDED];
/** LM-29: new versions the third release `gacpro-checks@2026.10.3` takes in place of the old ones (migration 686). */
export const SLICE_RULE_SET_V3_CHANGED = [R05_V2];
/** The 20 rules of the third release: the second's, with R05 at version 2. */
export const SLICE_RULE_SET_V3 = SLICE_RULE_SET_V2.map((r) => SLICE_RULE_SET_V3_CHANGED.find((n) => n.content.rule_id === r.content.rule_id) ?? r);
/** Every slice rule in force (the latest release's members). */
export const SLICE_RULE_SET = SLICE_RULE_SET_V3;
/** Every slice rule version ever released (what the content load inserts): the in-force set plus superseded versions. */
export const SLICE_RULE_VERSIONS = [...SLICE_RULE_SET_V2, ...SLICE_RULE_SET_V3_CHANGED];
export const ruleVersionId = (ruleId, version) => `${ruleId}@${version}`;
/** Every slice rule (default: all in force) as a version record; test_status from running its fixtures now (pure). */
export function sliceCatalog(rules = SLICE_RULE_SET) {
    return rules.map(({ content, fixtures, author }) => {
        const report = testRuleFixtures(content, fixtures);
        return {
            fixtures,
            record: {
                ...content,
                rule_version_id: ruleVersionId(content.rule_id, content.version),
                content_sha256: ruleContentSha256(content),
                fixtures_sha256: fixturesSha256(fixtures),
                test_status: report.passed ? 'passed' : 'failed',
                release_state: report.passed ? 'tested' : 'draft',
                author: author ?? AUTHOR,
                reviewer: null,
                reviewed_at: null,
            },
        };
    });
}
/** The first catalog release (ADR-0090 §5): the 19 V1 rules at version 1 (migration 675). */
export const SLICE_CATALOG_VERSION_V1 = 'gacpro-checks@2026.10.1';
/** LM-26: the second release — V1's 19 unchanged plus R20 (migration 684). */
export const SLICE_CATALOG_VERSION_V2 = 'gacpro-checks@2026.10.2';
/** LM-29: the third release — the second's 20 with R05 at version 2 (migration 686). */
export const SLICE_CATALOG_VERSION_V3 = 'gacpro-checks@2026.10.3';
/** The catalog release in force (the latest). */
export const SLICE_CATALOG_VERSION = SLICE_CATALOG_VERSION_V3;
/** Why each rule entered the second release (its change report). */
export const SLICE_CATALOG_V2_REASONS = {
    [R20.content.rule_id]: 'LM-26 (owner 2026-10-14): closes KSI-IAM-JIT with a read-only Privileged Access Manager read',
};
/** Why each rule changed in the third release (its change report). */
export const SLICE_CATALOG_V3_REASONS = {
    [R05_V2.content.rule_id]: 'LM-29 (owner 2026-10-14): zero privileged principals passes when a live Privileged Access Manager entitlement can grant an admin role; zero with none is still orphaned',
};
//# sourceMappingURL=catalog.js.map